import React, { useState, useEffect, useMemo } from "react";
import TableContainer from "Common/TableContainer";
import BreadCrumb from "Common/BreadCrumb";
import callApi from "Common/utils/apiCaller";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import PermissionSwitch from "Common/Components/PermissionLocks/PermissionSwitch";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Plus } from "lucide-react";
import { ToastContainer } from "react-toastify";

const RolesList = () => {
  const selectProperties = createSelector(
    (state: any) => state.Login,
    (profile) => ({
      permission: profile.permission,
    })
  );

  const { permission } = useSelector(selectProperties);

  const [rolesData, setRolesData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 30 });
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState<string>("");
  const [newRoleDescription, setNewRoleDescription] = useState<string>("");

  const navigate = useNavigate();

  const fetchRolesData = async () => {
    try {
      const result = await callApi("v1/role/list", "post", {
        pageNum: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filters: { is_deleted: false },
      });

      // const result = {
      //   status: "Success",
      //   data: {
      //     roleList: RolesData.filter((role) => !role.is_deleted),
      //     roleCount: RolesData.filter((role) => !role.is_deleted).length,
      //   },
      // };

      if (result.status === "Success") {
        setRolesData(result.data.roleList || []);
        setPageCount(Math.ceil(result.data.roleCount / pagination.pageSize));
        setTotalItems(result.data.roleCount);
      } else {
        // toast.error(result.data.message || "Error fetching roles.");
      }
    } catch (error) {
      toast.error("Failed to fetch roles.");
    }
  };

  useEffect(() => {
    fetchRolesData();
  }, [pagination]);

  const addNewRole = async () => {
    try {
      const result = await callApi("v1/role/new", "post", {
        role: {
          name: newRoleName,
          description: newRoleDescription,
          permissions: [],
          is_deleted: false,
        },
      });

      if (result.status === "Success") {
        toast.success("Role added successfully!");
        setNewRoleName("");
        setNewRoleDescription("");
        setShowModal(false);
        fetchRolesData();
      } else {
        toast.error(result.data.message || "Error adding role.");
      }
    } catch (error) {
      toast.error("Error adding role.");
    }
  };

  const handleDeleteRole = async (id: string) => {
    try {
      const result = await callApi(`v1/role/${id}/update`, "post", {
        role: { is_deleted: true },
      });
      if (result.status === "Success") {
        toast.success("Role deleted successfully!");
        fetchRolesData();
      } else {
        toast.error(result.data.message || "Error deleting role.");
      }
    } catch (error) {
      toast.error("Failed to delete role.");
    }
  };

  const handleEditRole = (role: any) => {
    console.log(role);

    navigate(`/roles/${role?._id}`);
  };

  const roleColumns = useMemo(
    () => [
      {
        header: "S.N.",
        accessorKey: "serial",
        cell: (cell: any) => cell.row.index + 1,
      },
      {
        header: "Role",
        accessorKey: "name",
        cell: (cell: any) => cell.getValue() || "---",
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: (cell: any) => cell.getValue() || "---",
      },
      {
        header: "Permissions",
        accessorKey: "permissions",
        cell: (cell: any) =>
          cell.getValue() && cell.getValue().length > 0
            ? cell.getValue().slice(0, 6).join(", ") +
              (cell.getValue().length > 3 ? "..." : "")
            : "No Permissions",
        className: "whitespace-nowrap max-w-full",
      },
      {
        header: "Edit Permissions",
        accessorKey: "edit",
        cell: ({ row }: any) => (
          <PermissionSwitch
            hasPermissions={permission.permissions}
            shouldHavePermissions={["role:edit"]}
            yes={
              <button
                className="text-blue-500 hover:text-blue-700"
                onClick={() => handleEditRole(row.original)}
              >
                Edit
              </button>
            }
            no={<span></span>}
          />
        ),
      },
      {
        header: "Delete Role",
        accessorKey: "delete",
        cell: ({ row }: any) => (
          <PermissionSwitch
            hasPermissions={permission.permissions}
            shouldHavePermissions={["role:delete"]}
            yes={
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => handleDeleteRole(row.original._id)}
              >
                Delete
              </button>
            }
            no={<span></span>}
          />
        ),
      },
    ],
    [permission.permissions]
  );

  const toggleModal = () => {
    setShowModal(!showModal);
    if (!showModal) {
      setNewRoleName("");
      setNewRoleDescription("");
    }
  };

  const isSubmitDisabled = !newRoleName.trim() || !newRoleDescription.trim();

  return (
     <PermissionSwitch
    hasPermissions={permission.permissions}
    shouldHavePermissions={["role:read"]}
    yes={
    <React.Fragment>
      <BreadCrumb title="Roles" pageTitle="Admin" version="" />
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold">Roles</h1>
            <PermissionSwitch
              hasPermissions={permission.permissions}
              shouldHavePermissions={["role:create"]}
              yes={
                <button
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={toggleModal}
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Add New Role</span>
                </button>
              }
              no={
                <button
                  type="button"
                  disabled={true}
                  className="text-grey btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={null}
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Add New Role</span>
                </button>
              }
            />
          </div>
        </div>
        <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
          <TableContainer
            isPagination={true}
            setPagination={setPagination}
            columns={roleColumns}
            data={rolesData}
            pageCount={pageCount}
            pagination={pagination}
            totalItems={totalItems}
            onPaginationChange={(updater: any) => {
              const nextState = updater(pagination);
              if (nextState.pageIndex !== pagination.pageIndex) {
                setPagination(nextState);
              }
            }}
            divclassName="overflow-x-auto"
            tableclassName="w-full whitespace-nowrap"
            theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
            thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
            tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
            PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
          />
        </div>
      </div>

      {/* Modal for adding new roles */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="p-6 bg-white rounded-md w-96">
            <h2 className="mb-4 text-xl font-bold">Add New Role</h2>
            <input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="Role Name"
              className="w-full mb-4 border form-input"
            />
            <textarea
              value={newRoleDescription}
              onChange={(e) => setNewRoleDescription(e.target.value)}
              placeholder="Role Description"
              className="w-full mb-4 border form-input"
              rows={3}
            />
            <div className="flex justify-end gap-3">
              <button
                className="text-black btn bg-slate-200"
                onClick={toggleModal}
              >
                Cancel
              </button>
              <button
                className={`btn ${
                  isSubmitDisabled
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-custom-500 text-white"
                }`}
                onClick={addNewRole}
                disabled={isSubmitDisabled}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </React.Fragment>
    }
    no={
      <div className="text-center mt-10 text-gray-500">
        You do not have permission to view roles.
      </div>
    }
  />
  );
};

export default RolesList;
